<script setup>
import { ref, computed, defineProps, defineEmits, defineExpose } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Search...'
  },
  id: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'input', 'keyup', 'clear-badge']);

const input = ref(null);

const showBadge = computed(() => {
  return props.modelValue && props.modelValue.trim();
});

const inputValue = computed(() => {
  return props.modelValue;
});

const handleInput = (event) => {
  emit('update:modelValue', event.target.value);
  emit('input', event);
};

const handleKeyup = (event) => {
  emit('keyup', event);
};

const clearBadge = () => {
  emit('update:modelValue', '');
  emit('clear-badge');
  input.value?.focus();
};

const focusInput = () => {
  input.value?.focus();
};

defineExpose({ focus: focusInput });
</script>

<template>
  <div class="badge-input">

    <div class="badge-input__icon">
      <slot name="icon">
        <i class="uil uil-search text-sm"></i>
      </slot>
    </div>


    <div v-if="showBadge" class="badge-input__badge bg-echoes-primary">
      "{{ modelValue }}"
      <button 
        @click="clearBadge"
        class="badge-input__badge-close"
        type="button"
      >
        <i class="uil uil-times text-xs"></i>
      </button>
    </div>


    <input
      ref="input"
      type="text"
      :id="id"
      :value="inputValue"
      :placeholder="showBadge ? '' : placeholder"
      :disabled="disabled"
      @input="handleInput"
      @keyup="handleKeyup"
      :class="[
        'badge-input__field',
        showBadge ? 'badge-input__field--with-badge' : '',
        disabled ? 'badge-input__field--disabled' : ''
      ]"
    />

  </div>
</template>

<style scoped lang="postcss">
.badge-input {
  @apply relative;
}

.badge-input__icon {
  @apply absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none
    text-white/80 z-10;
}

.badge-input__field {
  @apply w-full px-4 py-2.5 pl-11 text-sm rounded-[13px] h-11
    bg-echoes-background-black
    border border-echoes-border-gray
    text-white
    transition-all duration-200 ease-in-out;
  
  &:hover {
    @apply bg-echoes-background-black
      border-white/20
      text-white;
  }

  &:focus {
    @apply bg-echoes-background-black border-echoes-primary outline-none;
  }

  &::placeholder {
    @apply transition-all duration-200 text-white/50 hover:text-white/80;
  }

  &:focus::placeholder {
    @apply opacity-0 translate-x-1;
  }
}

.badge-input__field--with-badge {
  @apply text-transparent caret-transparent;
}

.badge-input__field--disabled {
  @apply opacity-60 cursor-not-allowed;

  &:hover {
    @apply bg-echoes-background-black
      border-echoes-border-gray
      shadow-none;
  }
}

.badge-input__badge {
  @apply absolute top-1/2 left-0 -translate-y-1/2 flex items-center px-3 py-1.5 ml-11 mr-2 
    text-white font-medium text-sm rounded-lg z-20 pointer-events-none h-7;
}

.badge-input__badge-close {
  @apply flex items-center justify-center w-5 h-5 ml-2 rounded-full
    text-white/80 hover:text-white bg-white/10 hover:bg-white/30
    transition-all duration-200 pointer-events-auto;
}
</style>
